/** @jsxImportSource @emotion/react */

import { type DragEventHandler, useRef, useState } from 'react';

import { GalleryUploadDropzone } from '@kaiizen/kai-shiki/react';

import {
  PROGRESS_UPDATE_DROPZONE_WRAP_STYLE,
  PROGRESS_UPDATE_FILE_LIST_STYLE,
  PROGRESS_UPDATE_FILE_NAME_STYLE,
  PROGRESS_UPDATE_FILE_REMOVE_STYLE,
  PROGRESS_UPDATE_FILE_ROW_STYLE,
} from './focusedProgressUpdateModalStyles';
import type { StagedProgressPhoto } from './progressUpdateModalTypes';

let stagedPhotoSequence = 0;

const stagePhotoFiles = (files: FileList | File[] | null | undefined): StagedProgressPhoto[] => {
  if (!files?.length) return [];
  return Array.from(files)
    .filter(file => file.type.startsWith('image/'))
    .map(file => ({ id: ++stagedPhotoSequence, file }));
};

export const ProgressUpdateMediaSection = ({
  stagedPhotos,
  onStagedPhotosChange,
}: {
  stagedPhotos: readonly StagedProgressPhoto[];
  onStagedPhotosChange: (photos: StagedProgressPhoto[]) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const addFiles = (files: FileList | File[] | null | undefined) => {
    const next = stagePhotoFiles(files);
    if (!next.length) return;
    onStagedPhotosChange([...stagedPhotos, ...next]);
  };

  const preventDefault: DragEventHandler = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  const onDragEnter: DragEventHandler = event => {
    preventDefault(event);
    setIsDragging(true);
  };

  const onDragLeave: DragEventHandler = event => {
    preventDefault(event);
    setIsDragging(false);
  };

  const onDrop: DragEventHandler = event => {
    preventDefault(event);
    setIsDragging(false);
    addFiles(event.dataTransfer.files);
  };

  const removePhoto = (id: number) => {
    onStagedPhotosChange(stagedPhotos.filter(photo => photo.id !== id));
  };

  return (
    <div
      onDragEnter={onDragEnter}
      onDragOver={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div css={PROGRESS_UPDATE_DROPZONE_WRAP_STYLE}>
        <GalleryUploadDropzone
          title='Drag files here, or browse'
          helperText='Photos only'
          state={isDragging ? 'dragging' : 'idle'}
          onActivate={() => inputRef.current?.click()}
          onClick={event => {
            event.stopPropagation();
            inputRef.current?.click();
          }}
        />
      </div>
      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        multiple
        hidden
        onChange={event => {
          addFiles(event.target.files);
          event.target.value = '';
        }}
      />
      {stagedPhotos.length
        ? (
          <div css={PROGRESS_UPDATE_FILE_LIST_STYLE}>
            {stagedPhotos.map(({ id, file }) => (
              <div key={id} css={PROGRESS_UPDATE_FILE_ROW_STYLE}>
                <span css={PROGRESS_UPDATE_FILE_NAME_STYLE}>{file.name}</span>
                <button
                  type='button'
                  css={PROGRESS_UPDATE_FILE_REMOVE_STYLE}
                  aria-label={`Remove ${file.name}`}
                  onClick={() => removePhoto(id)}
                >
                  <svg viewBox='0 0 24 24' aria-hidden='true'>
                    <path d='M6 6l12 12M18 6L6 18' />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )
        : null}
    </div>
  );
};
